import { Link } from "react-router-dom";
import { RefundPolicy } from "./RefundPolicy";
import { ArrowLeft, ArrowUpRight } from "lucide-react";

const PRODUCTS = [
  {
    id: "hoodie",
    name: "Hoodies",
    description:
      "A comfortable and stylish hoodie perfect for workouts and casual wear.",
    path: "/leapbodies/hoodie",
    cta: "View the hoodies",
    image: "/products/hoodie-black.jpg",
  },
  {
    id: "tee",
    name: "Tees",
    description:
      "A lightweight t-shirt designed for maximum comfort during workouts.",
    path: "/leapbodies/tee",
    cta: "View the tees",
    image: "/products/regular-black.jpg",
  },
];

const LOOKBOOK = [
  {
    src: "https://customer-assets-jt897jd0.emergentagent.net/job_clothing-brands-1/artifacts/8ki7t6cn_WechatIMG515.jpg",
    span: "lg:col-span-2",
    aspect: "aspect-[3/4]",
  },
  {
    src: "https://customer-assets-jt897jd0.emergentagent.net/job_clothing-brands-1/artifacts/uyjh8suw_WechatIMG516.jpg",
    span: "lg:col-span-2",
    aspect: "aspect-[3/4]",
  },
  {
    src: "https://customer-assets-jt897jd0.emergentagent.net/job_clothing-brands-1/artifacts/4l54lmxg_WechatIMG517.jpg",
    span: "lg:col-span-2",
    aspect: "aspect-[3/4]",
  },
  {
    src: "https://customer-assets-jt897jd0.emergentagent.net/job_clothing-brands-1/artifacts/1cljg9rk_WechatIMG518.jpg",
    span: "lg:col-span-3",
    aspect: "aspect-[4/3]",
  },
  {
    src: "https://customer-assets-jt897jd0.emergentagent.net/job_clothing-brands-1/artifacts/w35dqo8f_WechatIMG519.jpg",
    span: "lg:col-span-3",
    aspect: "aspect-[4/3]",
  },
];

export const Leapbodies = () => (
  <div data-testid="leapbodies-page" className="min-h-screen flex flex-col">
    <header className="border-b border-ink/10 px-6 py-4 bg-[#F9F8F5]/90 backdrop-blur-md">
      <div className="max-w-5xl mx-auto flex items-center justify-between">
        <Link
          to="/"
          data-testid="leapbodies-back-link"
          className="inline-flex items-center gap-2 text-sm text-mutedink hover:text-oxblood transition-colors duration-300"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Portfolio
        </Link>
        <span className="font-cinzel text-xs sm:text-sm tracking-[0.25em] text-ink">
          HAILEY <span className="text-bronze">&amp;</span> CHLOE
        </span>
      </div>
    </header>

    <main className="flex-1 px-6 py-16 sm:py-20">
      <div className="max-w-5xl mx-auto">
        <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-mutedink mb-4">
          A Hailey &amp; Chloe Holdings Project
        </p>
        <h1 className="font-serif font-light tracking-tight text-ink text-3xl sm:text-4xl lg:text-5xl">
          LEAPBODIES
        </h1>
        <p
          data-testid="leapbodies-tagline"
          className="mt-3 font-serif italic text-xl text-oxblood"
        >
          Mind Sharp, Body Strong.
        </p>
        <p className="mt-6 max-w-xl text-base font-light text-[#2A2A2A] leading-relaxed">
          Activewear built for progress, not perfection. LeapBodies makes
          comfortable, workout-ready essentials designed to support lasting
          change — in and out of the gym.
        </p>
        <a
          href="https://www.leapbodies.com"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="leapbodies-visit-link"
          className="mt-6 inline-flex items-center gap-2 border border-ink/25 text-ink px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] hover:border-oxblood hover:text-oxblood transition-colors duration-300"
        >
          Visit www.leapbodies.com
          <ArrowUpRight className="w-3.5 h-3.5" />
        </a>

        <div
          className="mt-14 grid sm:grid-cols-2 gap-6"
          data-testid="leapbodies-products"
        >
          {PRODUCTS.map((p) => {
            const inner = (
              <>
                <div className="overflow-hidden aspect-[4/5]">
                  <img
                    src={p.image}
                    alt={`LeapBodies ${p.name}`}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-5">
                  <h2 className="font-serif text-xl text-ink">{p.name}</h2>
                  <p className="mt-2 text-sm text-mutedink leading-relaxed">
                    {p.description}
                  </p>
                  {p.path && (
                    <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-oxblood">
                      {p.cta}
                      <ArrowUpRight className="w-3.5 h-3.5" />
                    </p>
                  )}
                </div>
              </>
            );
            const cls = "bg-white border border-ink/10 block";
            return p.path ? (
              <Link
                key={p.id}
                to={p.path}
                data-testid={`product-card-${p.id}`}
                className={cls}
              >
                {inner}
              </Link>
            ) : (
              <article
                key={p.id}
                data-testid={`product-card-${p.id}`}
                className={cls}
              >
                {inner}
              </article>
            );
          })}
        </div>

        <section
          data-testid="leapbodies-lookbook"
          className="mt-20 border-t border-ink/10 pt-14"
        >
          <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-mutedink mb-3">
            Lookbook
          </p>
          <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-ink mb-8">
            LeapBodies, worn out in the world.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-4">
            {LOOKBOOK.map((photo, i) => (
              <div
                key={photo.src}
                data-testid={`lookbook-photo-${i + 1}`}
                className={`overflow-hidden border border-ink/10 ${photo.span}`}
              >
                <img
                  src={photo.src}
                  alt={`LeapBodies lookbook — models wearing the tee, photo ${i + 1}`}
                  loading="lazy"
                  className={`w-full object-cover ${photo.aspect}`}
                />
              </div>
            ))}
          </div>
        </section>

        <RefundPolicy brand="LeapBodies" />
      </div>
    </main>

    <footer className="bg-[#111111] text-[#F9F8F5] px-6 py-8">
      <div className="max-w-5xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="font-cinzel text-sm tracking-[0.2em]">
          LEAP<span className="text-bronze">BODIES</span>
        </p>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#F9F8F5]/40">
          © Hailey and Chloe Holdings Inc.
        </p>
      </div>
    </footer>
  </div>
);
